import {
  BASELINE_THEME_TOKENS,
  parseColor,
  readableForeground,
  type ThemeTokens,
} from '@mlm/shared';

/**
 * The default store orange (#EE4D2D) that every storefront starts from and that
 * the `omni` preset seeds into `tenant_config.theme`. It is a storefront default
 * modelled on Shopee, so a prospect reads the store as a familiar marketplace;
 * it is not Omni's brand and not any client's.
 */
const DEFAULT_STORE_PRIMARY = parseColor(BASELINE_THEME_TOKENS.primary!);

/**
 * Whether the tenant's primary colour is still the default store orange,
 * written either way the admin can save it ("9.9 85% 55.5%" or "#EE4D2D").
 * A theme with no primary is not "on the default": it has nothing to apply.
 */
export function onDefaultStoreTheme(theme: ThemeTokens | null | undefined): boolean {
  const primary = theme?.primary?.trim();
  if (!primary) return false;
  try {
    const c = parseColor(primary);
    return (
      Math.abs(c.h - DEFAULT_STORE_PRIMARY.h) < 0.6 &&
      Math.abs(c.s - DEFAULT_STORE_PRIMARY.s) < 0.6 &&
      Math.abs(c.l - DEFAULT_STORE_PRIMARY.l) < 0.6
    );
  } catch {
    return false;
  }
}

/** What a tenant still on the default store theme carries over to the till. */
const IDENTITY_TOKENS = [
  'logoUrl',
  'logoUrlDark',
  'logoKey',
  'logoKeyDark',
  'faviconKey',
  'wordmark',
  'fontSans',
  'fontHeading',
  'fontBody',
  'fontLinks',
  'radius',
] as const satisfies ReadonlyArray<keyof ThemeTokens>;

/**
 * The theme the TILL applies, which is not always the tenant's whole theme.
 *
 * A tenant with colours of its own gets them on the till, rail included. A
 * tenant still on the default store orange does not: that orange belongs to the
 * storefront, so the till keeps its own look, which is Omni's (the `@mlm/ui`
 * baseline blue, the same as the admin, and the navy rail from `index.css`).
 * Only the tenant's identity carries over: logo, wordmark, favicon, fonts and
 * corner radius.
 */
export function tillTheme(theme: ThemeTokens | null | undefined): ThemeTokens | null {
  if (!theme) return null;
  if (!onDefaultStoreTheme(theme)) return theme;
  const kept: ThemeTokens = {};
  for (const key of IDENTITY_TOKENS) {
    if (theme[key] !== undefined) (kept as Record<string, unknown>)[key] = theme[key];
  }
  return kept;
}

/**
 * The till's icon rail colours, as `[cssVar, value]` pairs for `<html>`.
 *
 * The rail takes the tenant's Secondary colour (Admin → Layout & Theme). It
 * cannot simply read `--secondary` in CSS: the un-themed baseline for that
 * token is a light grey surface, so a tenant that never picked a secondary
 * would get a washed-out rail. Returning nothing here leaves the default from
 * `index.css` in place instead, Omni's navy.
 *
 * Pass the theme through `tillTheme` first, as the theme boot does, so a tenant
 * still on the default store theme keeps the navy rail too.
 *
 * Kept free of `./api` so it stays importable from a plain unit test.
 */
export function railCssVars(theme: ThemeTokens | null | undefined): Array<[string, string]> {
  const secondary = theme?.secondary?.trim();
  if (!secondary) return [];
  let foreground = theme?.secondaryForeground?.trim();
  if (!foreground) {
    try {
      foreground = readableForeground(parseColor(secondary));
    } catch {
      // A hand-typed value the parser does not know: keep the default rail
      // rather than pair an unknown background with a guessed text colour.
      return [];
    }
  }
  return [
    ['--pos-rail', secondary],
    ['--pos-rail-foreground', foreground],
  ];
}

/**
 * Whether the rail (and the login backdrop, which shares its colour) is a dark
 * surface, i.e. whether the tenant's dark-surface logo is the right one to put
 * on it. The default rail is dark; a tenant's own rail is dark when the text
 * chosen for it is light.
 */
export function railIsDark(theme: ThemeTokens | null | undefined): boolean {
  const foreground = railCssVars(theme).find(([name]) => name === '--pos-rail-foreground')?.[1];
  if (!foreground) return true;
  try {
    return parseColor(foreground).l > 50;
  } catch {
    return true;
  }
}
